import { NavigationContainer } from "@react-navigation/native";
import * as Notifications from "expo-notifications";
import React, { useEffect, useRef, useState } from "react";
import { Linking } from "react-native";
import DropdownAlert from "react-native-dropdownalert";
import { ThemeProvider } from "react-native-elements";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
	LOGOUT,
	NAVIGATION,
	ON_APP_BLUR,
	ON_APP_FOCUS,
	RESET_AND_SUBSCRIBE
} from "../actions/types";
import {
	ABOUT_ROUTE,
	AD_ROUTE,
	APPLE_STORE_URL,
	CAMERA_ROUTE,
	COMMUNITIES_ROUTE,
	CGU_ROUTE,
	CITIES_ROUTE,
	COMMUNITIES_MAPS_ROUTE,
	DEEPLINK_APP_URL,
	DEEPLINK_URL_SCHEME,
	EVENT_DETAILS_ROUTE,
	GOOGLE_PLAY_URL,
	LOCAL_LIFE_ROUTE,
	MAINTENANCE_ROUTE,
	MY_CONVERSATIONS_ROUTE,
	MY_PROFILE_ROUTE,
	PROFILE_ROUTE,
	PWA_DEEPLINK_APP_URL,
	REGISTER_ROUTE,
	ROLE_GUEST,
	URL_PREFIX,
	YOUTUBE_ROUTE,
	ZOOM_IMAGE_ROUTE
} from "../config/constants";
import env from "../config/env";
import { getAppNavigationSelector } from "../selectors";
// Services
import { StatusBar } from "expo-status-bar";
import DeepLinkManagement from "../services/DeepLinkManagement";
import ErrorManagement from "../services/ErrorManagement";
import i18n from "../services/i18n";
import PushNotifications from "../services/PushNotifications";
import openSettings from "../services/Settings";
import {
	COLOR2,
	GREEN_COLOR,
	isAndroid,
	isElectron,
	isWeb
} from "../styles/commonStyles";
import isDevelopment from "../utils/electron-is-dev";
import { openURL } from "../utils/UrlUtil";
import { createRootAppStackNavigator } from "./AppNavigator";
import CompactInstallPrompt from "../components/CompactInstallPrompt";
import EnableNotificationsPrompt from "../components/EnableNotificationsPrompt";

const DEEPLINK_DEV_URL = env.deepLinkUrl || "exp://localhost:19000/--/";

const theme = {
	colors: {
		primary: COLOR2
	}
};

const isDevElectron = isElectron && isDevelopment;

// Gets the current screen from navigation state
const getActiveRouteName = state => {
	const route = state.routes[state.index];

	if (route.state) {
		// Dive into nested navigators
		return getActiveRouteName(route.state);
	}

	return route.name;
};

const getActiveRoute = state => {
	const route = state.routes[state.index];

	if (route.state) {
		// Dive into nested navigators
		return getActiveRouteName(route.state);
	}

	return route;
};

export default function AppNavigationContainer() {
	const dispatch = useDispatch();

	const [screen, setScreen] = useState(null);
	const [params, setParams] = useState(null);
	const [isReady, setIsReady] = useState(false);
	// React Navigation 7 only fires `onReady` once a navigator has actually
	// mounted (v6 fired it as soon as linking resolved), so gating the navigator
	// on `isReady` deadlocks: no navigator means no `onReady`, and no `onReady`
	// means no navigator. Mount on the second render instead, which reproduces
	// the v6 ordering where `DropdownAlert` has already populated its ref by the
	// time `ErrorManagement` reads it.
	const [isMounted, setIsMounted] = useState(false);

	const {
		user,
		accessDenied,
		lang,
		displayPreview,
		keyboardOpen,
		communityDetails,
		isFetching
	} = useSelector(getAppNavigationSelector, shallowEqual);

	const routeNameRef = useRef();
	const navigationRef = useRef();
	const dropdownAlertRef = useRef();

	const navigation = navigationRef?.current;

	const onFocusFunction = () => {
		dispatch({ type: ON_APP_FOCUS });
	};

	const onBlurFunction = () => {
		dispatch({ type: ON_APP_BLUR });
	};

	useEffect(() => {
		if (isWeb) {
			window.addEventListener("focus", onFocusFunction);
			window.addEventListener("blur", onBlurFunction);
		}
		return () => {
			if (isWeb) {
				window.removeEventListener("focus", onFocusFunction);
				window.removeEventListener("blur", onBlurFunction);
			}
		};
	}, []);

	useEffect(() => {
		if (lang) {
			(async () => {
				await i18n.setLanguage(lang);
			})();
		}
	}, [lang]);

	const onTap = message => {
		if (message.payload) {
			if (user && user !== ROLE_GUEST && message.payload.id) {
				navigation?.navigate("ChatTeamScreen", {
					to: message.payload.id
				});
			} else if (user && user !== ROLE_GUEST && message.payload.pseudo) {
				navigation?.navigate("ChatScreen", {
					pseudo: message.payload.pseudo
				});
			} else if (user && user !== ROLE_GUEST && message.payload.eventId) {
				navigation?.navigate("ChatGrpScreen", {
					eventId: message.payload.eventId
				});
			} else if (message.payload.dropDownAlertWarn === "obsoleteversionios") {
				openURL(dispatch, APPLE_STORE_URL);
			} else if (
				message.payload.dropDownAlertWarn === "obsoleteversionandroid"
			) {
				openURL(dispatch, GOOGLE_PLAY_URL);
			} else if (
				message.payload.dropDownAutoClearAlertInfo === "locationinfo" ||
				message.payload.dropDownAutoClearAlertInfo === "trackinfo" ||
				message.payload.dropDownAutoClearAlertInfo === "notificationinfo" ||
				message.payload.dropDownAutoClearAlertInfo === "photosinfo" ||
				message.payload.dropDownAutoClearAlertInfo === "camerainfo"
			) {
				openSettings({ message, dispatch });
			} else if (
				message.payload.dropDownAutoClearAlertWarn === "guestforbidden"
			) {
				dispatch({
					type: LOGOUT,
					value: RESET_AND_SUBSCRIBE
				});
			}
		}
	};

	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		if (isReady && navigation) {
			const state = navigation.getRootState();

			// Save the initial route name
			routeNameRef.current = getActiveRouteName(state);
		}
	}, [isReady]);

	// Workaround notification android does not work
	if (isAndroid) {
		const lastNotificationResponse =
			Notifications.useLastNotificationResponse();
		useEffect(() => {
			if (
				lastNotificationResponse?.notification?.request?.content?.data
					?.screen &&
				lastNotificationResponse.actionIdentifier ===
					Notifications.DEFAULT_ACTION_IDENTIFIER
			) {
				setScreen(
					lastNotificationResponse.notification.request.content.data.screen
				);
				setParams(
					lastNotificationResponse.notification.request.content.data.params
				);
			}
		}, [lastNotificationResponse]);
	}

	const prefix = URL_PREFIX ? URL_PREFIX + "/" : "";

	const prefixes = __DEV__
		? [
				PWA_DEEPLINK_APP_URL,
				DEEPLINK_APP_URL,
				DEEPLINK_URL_SCHEME,
				DEEPLINK_DEV_URL
		  ]
		: [PWA_DEEPLINK_APP_URL, DEEPLINK_APP_URL, DEEPLINK_URL_SCHEME];

	const loading = {
		prefixes,
		config: {
			screens: {
				LoadingScreen: isDevElectron ? "" : `${URL_PREFIX}`,
				MaintenanceScreen: isDevElectron
					? MAINTENANCE_ROUTE
					: `${prefix}${MAINTENANCE_ROUTE}`
			}
		}
	};

	const landing = {
		prefixes,
		config: {
			screens: {
				CitiesModal: isDevElectron ? "" : `${URL_PREFIX}${CITIES_ROUTE}`,
				CommunitiesModal: isDevElectron
					? ""
					: `${URL_PREFIX}${COMMUNITIES_ROUTE}`,
				CommunitiesMapModal: isDevElectron
					? ""
					: `${URL_PREFIX}${COMMUNITIES_MAPS_ROUTE}`,
				AppInfosScreen: isDevElectron ? "" : `${prefix}${ABOUT_ROUTE}`,
				CGUScreen: isDevElectron ? "" : `${prefix}${CGU_ROUTE}`,
				AdScreen: isDevElectron
					? ""
					: `${prefix}${LOCAL_LIFE_ROUTE}/:cityUri/:adUri`,
				// SelectTemplateDateScreen: isDevElectron
				//   ? ""
				//   : `${prefix}parametres-publication`,
				EventDetailsScreen: isDevElectron
					? ""
					: `${prefix}${EVENT_DETAILS_ROUTE}`,
				ImageZoomScreen: isDevElectron ? "" : `${prefix}${ZOOM_IMAGE_ROUTE}`,
				MaintenanceScreen: isDevElectron ? "" : `${prefix}${MAINTENANCE_ROUTE}`,
				LandingScreen: isDevElectron ? "" : `${URL_PREFIX}:communityUri`,
				LoginModal: isDevElectron ? "" : `${URL_PREFIX}:communityUri/connexion`,
				ResetPasswordScreen: isDevElectron
					? ""
					: `${URL_PREFIX}:communityUri/reinitialisation`,
				CommunityDetailsScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/informations`
			}
		},
		subscribe(listener) {
			const urlListener = Linking.addEventListener("url", ({ url }) =>
				listener(url)
			);

			return () => {
				if (urlListener) {
					urlListener.remove();
				}
			};
		}
	};

	const registerUser = {
		prefixes,
		config: {
			screens: {
				ProfileUpdateScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/${REGISTER_ROUTE}`
			}
		}
	};

	const authenticated = {
		prefixes: [
			PWA_DEEPLINK_APP_URL,
			DEEPLINK_APP_URL,
			DEEPLINK_URL_SCHEME
			// DEEPLINK_DEV_URL
		],
		config: {
			screens: {
				ProfileUpdateScreen: isDevElectron
					? ""
					: `${prefix}${MY_PROFILE_ROUTE}`,
				CameraScreen: isDevElectron ? "" : `${prefix}${CAMERA_ROUTE}`,
				// The pseudo is optional: the profile is usually opened with a userId
				ProfileScreen: isDevElectron
					? ""
					: `${prefix}${PROFILE_ROUTE}/:pseudo?`,
				ProfileTeamScreen: isDevElectron ? "" : `${prefix}${PROFILE_ROUTE}`,
				ImageZoomScreen: isDevElectron ? "" : `${prefix}${ZOOM_IMAGE_ROUTE}`,
				YoutubeScreen: isDevElectron ? "" : `${prefix}${YOUTUBE_ROUTE}`,
				CGUScreen: isDevElectron ? "" : `${prefix}${CGU_ROUTE}`,
				MaintenanceScreen: isDevElectron ? "" : `${prefix}${MAINTENANCE_ROUTE}`,
				ChatTeamScreen: isDevElectron
					? ""
					: `${prefix}${MY_CONVERSATIONS_ROUTE}/conversation`,
				ChatScreen: isDevElectron
					? ""
					: `${prefix}${MY_CONVERSATIONS_ROUTE}/conversation/:pseudo`,
				ChatGrpScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/conversations-de-groupe/:eventId`,
				PdfWelcomeScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/pdf-bienvenue`,
				SelectActivityScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/actualites/instants-partage`,
				CreateTemplateScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/actualites/creation-modele`,
				SelectProposalScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/actualites/demandes`,
				SelectTemplateScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/actualites/modeles`,
				SelectTemplateDateScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/actualites/parametres-publication`,
				PostScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/actualites/edition-publication`,
				EventInterestedUsersScreen: isDevElectron
					? ""
					: `${prefix}:communityUri/actualites/publication`,
				EventDetailsScreen: isDevElectron ? "" : `${prefix}detail-publication`,
				AdDetailsScreen: isDevElectron ? "" : `${prefix}${AD_ROUTE}`,
				AdScreen: isDevElectron
					? ""
					: `${prefix}${LOCAL_LIFE_ROUTE}/:cityUri/:adUri`,
				TabScreen: {
					screens: {
						LiveTab: {
							screens: {
								LiveScreen: isDevElectron
									? ""
									: `${prefix}:communityUri/actualites`,
								SettingsScreen: `${prefix}:communityUri/parametres`
							}
						},
						ChatTab: {
							screens: {
								ChatOwnScreen: isDevElectron
									? ""
									: `${prefix}${MY_CONVERSATIONS_ROUTE}`,
								ChatGroupScreen: isDevElectron
									? ""
									: `${prefix}:communityUri/conversations-de-groupe`,
								ChatEmployeesScreen: isDevElectron
									? ""
									: `${prefix}:communityUri/a-mon-service`,
								ChatNeighborsScreen: isDevElectron
									? ""
									: `${prefix}:communityUri/mes-voisins`
							}
						},
						AdsTab: {
							screens: {
								AdsScreen: isDevElectron
									? ""
									: `${prefix}:communityUri/vie-locale`,
								AdsMapScreen: isDevElectron
									? ""
									: `${prefix}:communityUri/a-proximite`
							}
						},
						MeteoTab: {
							screens: {
								MeteoScreen: isDevElectron ? "" : `${prefix}:communityUri/meteo`
							}
						},
						CommunityDetailsTab: {
							screens: {
								AppInfosScreen: isDevElectron ? "" : `${prefix}${ABOUT_ROUTE}`,
								CommunityDetailsScreen: isDevElectron
									? ""
									: `${prefix}:communityUri/informations`
							}
						}
					}
				}
			}
		},
		subscribe(listener) {
			if (screen) {
				// Workaround : to avoid navigation to freeze
				setTimeout(() => {
					navigation?.navigate(screen, params);
					setScreen(null);
					setParams(null);
				}, 10);
			}

			const urlListener = Linking.addEventListener("url", ({ url }) =>
				listener(url)
			);

			const _handleNotification = response => {
				if (response?.notification?.request?.content?.data?.screen) {
					navigation?.navigate(
						response.notification.request.content.data.screen,
						response.notification.request.content.data.params
					);
				}
			};
			const _notificationSubscription =
				Notifications.addNotificationResponseReceivedListener(
					_handleNotification
				);

			return () => {
				_notificationSubscription.remove();
				if (urlListener) {
					urlListener.remove();
				}
			};
		}
	};

	const state = {
		routes: []
	};

	return (
		<NavigationContainer
			ref={navigationRef}
			onReady={() => setIsReady(true)}
			linking={
				Boolean(!user || isFetching)
					? !accessDenied
						? loading
						: landing
					: user.needUpdate
					? registerUser
					: authenticated
			}
			initialState={state}
			onStateChange={state => {
				const previousRouteName = routeNameRef.current;
				const currentRouteName = getActiveRouteName(state);

				if (previousRouteName !== currentRouteName) {
					const route = getActiveRoute(state);
					if (route instanceof Object) {
						// The navigation state is frozen: copy the route without its path
						// instead of deleting it (TypeError on a deep link with a path)
						const { path, ...others } = route;
						dispatch({
							type: NAVIGATION,
							...others
						});
					} else {
						dispatch({
							type: NAVIGATION,
							name: route
						});
					}
				}

				// Save the current route name for later comparision
				routeNameRef.current = currentRouteName;
			}}
		>
			{isMounted && (
				<ThemeProvider theme={theme}>
					{createRootAppStackNavigator({
						dispatch,
						user,
						accessDenied,
						navigation,
						displayPreview,
						keyboardOpen,
						communityDetails,
						isFetching
					})}
					<ErrorManagement
						navigationRef={navigationRef}
						dropDownHolder={dropdownAlertRef.current}
					/>
				</ThemeProvider>
			)}
			<DeepLinkManagement />
			<PushNotifications />
			<DropdownAlert
				ref={dropdownAlertRef}
				// `@react-navigation/stack` v7 puts each card's header in a
				// `zIndex: 1` container (CardContainer, new in v7), which draws it
				// over the alert. DropdownAlert leaves `zIndex` unset by default, so
				// it has to claim a higher one to stay on top; `elevation` is the
				// Android counterpart (its default is 1).
				zIndex={999}
				elevation={9}
				containerStyle={{ backgroundColor: GREEN_COLOR }}
				tapToCloseEnabled={true}
				closeInterval={6000}
				onTap={onTap}
				messageNumOfLines={5}
				updateStatusBar={false}
			/>
			<StatusBar style="dark" />
			{isWeb && <CompactInstallPrompt />}
			{isWeb && <EnableNotificationsPrompt />}
		</NavigationContainer>
	);
}
